// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PropertyEditorApi.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import editorFactories from 'share/factories/Izenda.PropertiesFieldEditorFactories';
import {
  createDropDownSchema,
  createPopupSettingSchema,
  createCheckBoxSchema,
  createSelectSchema,
  createNumberSchema,
  createColorPaletteSettingSchema,
  createOrientationSettingsSchema
} from 'share/utils/Izenda.PropsEditorGuiSchemaFunctions';

export default {
  /**
   * Register property editor with its factory function to create a custom property editor inside
   * Report Designer's property panel.
   * @param {string} type Editor type key
   * @param {Function} factory Function to create editor component
   * @example <caption>Register a text input property editor</caption>
   * registerPropertyEditor('text', function (type, props) {
   *  return {
   *    component: Input,
   *    props: {
   *      className: 'form-control',
   *      onChange: props.onChange
   *    }
   *  };
   * })
   */
  registerPropertyEditor(type: string, factory: (type: string, props: Object) => Object): void {
    editorFactories.containKey(type) && editorFactories.unregister(type);
    editorFactories.register(type, factory);
  },

  /**
   * Create a dropdown editor schema in Properties editor panel.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of label
   * @param {string} [props.info] The information tooltip of dropdown editor
   * @param {Any} props.value The value of selected item of dropdown editor
   * @param {Object} props.props The component properties
   * @param {boolean} [props.isHidden=false] Hide the editor or not.
   * @example <caption>An example of how using the function to create a dropdown list field schema in report part property schema</caption>
   *  {
   *       factory: createDropDownSchema,
   *       title: 'Example Dropdown',
   *       value: 'getChartType',
   *       props: {
   *         defaultValue: 'value1',
   *         options: ['value1', 'value2']
   *       }
   *  }
   */
  createDropDownPropertySchema: createDropDownSchema,

  /**
   * Create a popup editor schema in Properties editor panel. It appears as a label and a gear button which opens the popup dialog.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of label
   * @param {string} [props.info] The information tooltip of editor
   * @param {Any} props.value The value of selected item of editor
   * @param {Object} props.props The component computational properties
   * @param {Object} props.staticProps The component's static properties
   * @param {boolean} [props.isHidden=false] Hide the editor or not.
   * @example
   * {
   *   factory: createPopupSettingSchema,
   *   title: 'Border',
   *   value: 'getBorder',
   *   props: {
   *     reportPartType: REPORT_PART_TYPE.Chart,
   *     title: 'Border Settings',
   *     // The Border settings dialog uses chart properties to render the preview
   *     allChartProps: 'getAllChartProps',
   *     // Getter function or property to determine whether the setting exists
   *     hadSetting: 'hadSettingBorder',
   *     // Function to reset the setting value
   *     fnResetDefaultValue: 'fnResetBorderDefaultValue',
   *     // Getter function to retrieve value
   *     loadValue: 'getBorder'
   *   },
   *   staticProps: {
   *     popupTitle: 'Border Settings',
   *     popupContent: ChartBorderSettings
   *   }
   * }
   */
  createPopupSettingPropertySchema: createPopupSettingSchema,

  /**
   * Create a checkbox editor schema in Properties editor panel. It appears as a label and a checkbox.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of label
   * @param {string} [props.info] The information tooltip of editor
   * @param {Any} props.value The value of selected item of editor
   * @param {boolean} [props.isHidden=false] Hide the editor or not.
   * @example
   *  izSeparator: {
   *     factory: createCheckBoxSchema,
   *     title: 'Use Separator',
   *     // 'getValueByKey' is a default getter function of Chart that allows to get the value of a property of chart properties by its field name, ie. izSeparator
   *     value: 'getValueByKey'
   *  }
   */
  createCheckBoxPropertySchema: createCheckBoxSchema,

  /**
   * Create a select editor schema in Properties editor panel. It appears as a label and select control.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of editor
   * @param {string} [props.info] The information tooltip of editor
   * @param {Any} props.value The value of selected item of editor
   * @param {Object} props.props The component computational properties
   * @param {Object} props.staticProps The component's static properties
   * @param {boolean} [props.isHidden=false] Hide the editor or not.
   * @example <caption>Create a selection list of state with default blank option is All</caption>
   * {
   *     factory: createSelectSchema,
   *     title: 'State',
   *     value: 'getValueByKey',
   *     props: {
   *       options: 'getStateByCountry'
   *     },
   *     staticProps: {
   *       blankOption: true,
   *       blankOptionText: 'All'
   *     }
   *   }
   */
  createSelectPropertySchema: createSelectSchema,

  /**
   * Create a number editor schema in Properties editor panel. It appears as a label and number input control.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of label
   * @param {string} [props.info] The information tooltip of editor
   * @param {Any} props.value The value of selected item of editor
   * @param {Object} props.props The component properties
   * @param {boolean} [props.isHidden=false] Hide the editor or not.
   * @example
   * {
   *     factory: createNumberSchema,
   *     title: 'Item Per Row',
   *     value: 'getValueByKey',
   *     props: {
   *       min: 1,
   *       max: 20,
   *       isDecimal: false
   *     }
   * }
   */
  createNumberPropertySchema: createNumberSchema,

  /**
   * Create a color palette editor schema in Properties editor panel. It appears as a label and color palette input control.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of label
   * @param {string} [props.info] The information tooltip of editor
   * @param {Any} props.value The value of selected item of editor
   * @param {Object} props.props The component properties
   * @param {boolean} [props.isHidden=false] Hide the editor or not.
   * @example
   * {
   *     factory: createColorPaletteSettingSchema,
   *     title: 'Color Theme',
   *     value: 'getColorTheme',
   *     props: {
   *       selectionPopupTitle:'Select Color Theme',
   *       defaultColorThemeLabel:'No Theme',
   *       displaySettingStatus: true,
   *       needMaskDefault: false,
   *       defaultColorPalette: null,
   *       hiddenByState: true
   *     }
   * }
   */
  createColorPaletteSettingPropertySchema: createColorPaletteSettingSchema,

  /**
   * Create a text orientation editor schema in Properties editor panel. It appears as a label and text orientation input control.
   * This function is used as the factory property value of propertySchema of report part/style configuration
   * @param {Object} props The editor properties
   * @param {string} props.title The title of label
   * @param {string} [props.info] The information tooltip of editor
   * @param {Any} props.value The value of selected item of editor
   * @param {boolean} [props.isHidden=false] Hide the editor or not
   * @example
   * {
   *     factory: createOrientationSettingsSchema,
   *     title: 'Label Orientation',
   *     value: 'getValueByKey'
   * }
   */
  createOrientationSettingPropertySchema: createOrientationSettingsSchema
};



// WEBPACK FOOTER //
// ./ui/api/Izenda.PropertyEditorApi.js